import {debounce} from '@github/mini-throttle'
import type {CodeSymbol, TreeNode} from '@github-ui/code-nav'
import {AllShortcutsEnabled} from '@github-ui/code-view-shared/components/AllShortcutsEnabled'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {filterSort} from '@github-ui/filter-sort'
import {compare, fuzzyScore} from '@github-ui/fuzzy-filter'
import {ScopedCommands} from '@github-ui/ui-commands'
import {FilterIcon, XCircleFillIcon, XIcon} from '@primer/octicons-react'
import {IconButton, TextInput} from '@primer/react'
import React, {useCallback, useDeferredValue, useEffect, useMemo, useRef, useState} from 'react'

import {useFocusSymbolPane} from '../../../../hooks/use-focus-symbol-pane'
import styles from './CodeNavSymbolNavigation.module.css'
import {CodeNavSymbolTree} from './CodeNavSymbolTree'
import {JumpToItemList} from './JumpToItemList'
import {SymbolZeroState} from './SymbolZeroState'

// Memoize to prevent re-renders when only filterText changes (list uses deferredFilterText)
const MemoizedJumpToItemList = React.memo(JumpToItemList)

// Stable reference for leading visual to avoid recreating on each render
function FilterIconVisual() {
  return <FilterIcon aria-hidden="true" />
}

export const symbolsHeaderId = 'symbols-pane-header'
/**
 * An action menu that allows the user to navigate to certain sections of a given
 * file.
 *
 * This was modeled after RefSelector, but couldn't be shoehorned into a generic
 * ActionMenu component because it needs to have different functionality and specific
 * visual rendering. It could potentially be made into a generic ActionMenu component
 * where RefSelector and this component could both use it, but it didn't seem worth the
 * effort.
 *
 */
export function CodeNavSymbolNavigation({
  codeSymbols,
  onSymbolSelect,
  treeSymbols,
  autoFocusSearch,
  onClose,
}: {
  codeSymbols: CodeSymbol[]
  onSymbolSelect: (sym: CodeSymbol) => void
  treeSymbols: TreeNode[]
  autoFocusSearch: boolean
  onClose: () => void
}) {
  const [filterText, setFilterText] = useState('')
  // Defer filter text to keep typing responsive while filtering happens in background
  const deferredFilterText = useDeferredValue(filterText)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  // Filter symbols using deferred value for responsiveness
  const filteredCodeSymbols = useMemo(() => {
    if (deferredFilterText === '') {
      return codeSymbols
    }
    return getFilteredCodeSymbols(deferredFilterText, codeSymbols)
  }, [deferredFilterText, codeSymbols])

  const hasFilteredSymbols = filteredCodeSymbols?.length > 0
  const hasAnyCodeSymbols = codeSymbols?.length > 0
  const hasLocalCodeSymbols = treeSymbols.length > 0
  const hasAnySymbols = hasAnyCodeSymbols || hasLocalCodeSymbols
  const listIsFilteredOrEmpty = !hasLocalCodeSymbols || filterText !== ''
  // Compute aria-controls here to avoid passing hasFilteredSymbols to input
  const ariaControls = !hasFilteredSymbols && listIsFilteredOrEmpty ? 'filter-zero-state' : 'filter-results'
  const ariaActivedescendant = focusedIndex === -1 ? undefined : `jump-to-item-${focusedIndex}`

  // Stable callbacks for memoized input component
  const handleClear = useCallback(() => {
    setFilterText('')
    setFocusedIndex(-1)
  }, [])

  const handleChange = useCallback((value: string) => {
    setFilterText(value)
    setFocusedIndex(-1)
  }, [])

  // Stable reference for onSymbolSelect to prevent list item re-renders
  const onSymbolSelectRef = useRef(onSymbolSelect)
  useEffect(() => {
    onSymbolSelectRef.current = onSymbolSelect
  }, [onSymbolSelect])

  const stableOnSymbolSelect = useCallback((sym: CodeSymbol) => {
    onSymbolSelectRef.current(sym)
  }, [])

  // Stable reference for onClose to prevent header/handler re-renders
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const stableOnClose = useCallback(() => {
    onCloseRef.current()
  }, [])

  // Keyboard navigation must use current state values, not refs, to avoid race conditions
  // when typing and navigating simultaneously. The input re-renders anyway for aria-activedescendant.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'ArrowDown' || ((e.key === 'N' || e.key === 'n') && e.ctrlKey)) {
        const nextIndex = Math.min(focusedIndex + 1, filteredCodeSymbols.length - 1)
        setFocusedIndex(nextIndex)
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowUp' || ((e.key === 'P' || e.key === 'p') && e.ctrlKey)) {
        const nextIndex = Math.max(focusedIndex - 1, 0)
        setFocusedIndex(nextIndex)
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'Enter' && filteredCodeSymbols[focusedIndex]) {
        stableOnSymbolSelect(filteredCodeSymbols[focusedIndex])
      }
    },
    [filteredCodeSymbols, focusedIndex, stableOnSymbolSelect],
  )

  return (
    <ScopedCommands commands={{'github:cancel': stableOnClose}}>
      {/* eslint-disable-next-line github/a11y-role-supports-aria-props */}
      <div className={styles.container} aria-labelledby={symbolsHeaderId}>
        <SymbolPaneHeader onClose={stableOnClose} />
        {hasAnySymbols && (
          <p className={styles.description}>
            Find definitions and references for functions and other symbols in this file by clicking a symbol below or
            in the code.
          </p>
        )}
        {(hasAnyCodeSymbols || filterText !== '') && (
          <MemoizedSymbolFilterInput
            filterText={filterText}
            ariaControls={ariaControls}
            ariaActivedescendant={ariaActivedescendant}
            autoFocusSearch={autoFocusSearch}
            onClear={handleClear}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
        )}
        {!listIsFilteredOrEmpty && (
          <div className={styles.treeWrapper}>
            <CodeNavSymbolTree treeSymbols={treeSymbols} onTreeSymbolSelect={stableOnSymbolSelect} />
          </div>
        )}
        {hasFilteredSymbols && listIsFilteredOrEmpty && (
          <MemoizedJumpToItemList
            codeSymbols={filteredCodeSymbols}
            filterText={deferredFilterText}
            onSelect={stableOnSymbolSelect}
            focusedIndex={focusedIndex}
          />
        )}
        {!hasFilteredSymbols && listIsFilteredOrEmpty && (
          <span className={styles.zeroStateWrapper}>
            <SymbolZeroState filterText={filterText} />
          </span>
        )}
      </div>
    </ScopedCommands>
  )
}

// Memoized header - only re-renders when onClose changes
const SymbolPaneHeader = React.memo(function SymbolPaneHeader({onClose}: {onClose: () => void}) {
  return (
    <div className={styles.headerRow}>
      <h2 className={styles.headerTitle} id={symbolsHeaderId} tabIndex={-1}>
        Symbols
      </h2>
      <IconButton
        aria-label="Close symbols"
        tooltipDirection="w"
        icon={XIcon}
        className={styles.closeButton}
        onClick={onClose}
        variant="invisible"
      />
    </div>
  )
})

function Shortcuts() {
  return (
    <AllShortcutsEnabled>
      <span className={styles.shortcutsBox}>
        <kbd>r</kbd>
      </span>
    </AllShortcutsEnabled>
  )
}

// Memoized input - only re-renders when filterText changes or aria attributes update
function SymbolFilterInput({
  filterText,
  ariaControls,
  ariaActivedescendant,
  autoFocusSearch,
  onClear,
  onChange,
  onKeyDown,
}: {
  filterText: string
  ariaControls: string
  ariaActivedescendant: string | undefined
  autoFocusSearch: boolean
  onClear: () => void
  onChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
}) {
  const filterSymbolsRef = useRef<HTMLInputElement>(null)
  const {sendRepoKeyDownEvent} = useReposAnalytics()

  useEffect(() => {
    if (autoFocusSearch) {
      filterSymbolsRef.current?.focus()
    }
  }, [autoFocusSearch])

  useFocusSymbolPane(() => {
    filterSymbolsRef.current?.focus()
  })

  // Debounced analytics to avoid sending events on every keystroke
  const debouncedSendStats = useMemo(
    () =>
      debounce(() => {
        sendRepoKeyDownEvent('BLOB_SYMBOLS_MENU.FILTER_SYMBOLS')
      }, 400),
    [sendRepoKeyDownEvent],
  )

  // Cancel pending debounced calls on unmount
  useEffect(() => {
    return () => {
      debouncedSendStats.cancel()
    }
  }, [debouncedSendStats])

  const hasFilterText = filterText !== ''
  const trailingAction = useMemo(() => {
    return hasFilterText ? (
      <TextInput.Action
        onClick={onClear}
        icon={XCircleFillIcon}
        aria-label="Clear input"
        data-testid="clear-search"
        className={styles.TextInput_Action}
      />
    ) : (
      <></>
    )
  }, [hasFilterText, onClear])

  const trailingVisual = useMemo(() => {
    return hasFilterText ? undefined : Shortcuts
  }, [hasFilterText])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
      debouncedSendStats()
    },
    [onChange, debouncedSendStats],
  )

  return (
    <TextInput
      block
      leadingVisual={FilterIconVisual}
      ref={filterSymbolsRef}
      trailingAction={trailingAction}
      trailingVisual={trailingVisual}
      className={styles.filterInput}
      placeholder="Filter symbols"
      value={filterText}
      name="Filter symbols"
      aria-label="Filter symbols"
      aria-controls={ariaControls}
      aria-expanded="true"
      aria-autocomplete="list"
      aria-activedescendant={ariaActivedescendant}
      onKeyDown={onKeyDown}
      role="combobox"
      onChange={handleInputChange}
    />
  )
}

const MemoizedSymbolFilterInput = React.memo(SymbolFilterInput)

function getFilteredCodeSymbols(searchTerm: string, codeSymbols: CodeSymbol[]): CodeSymbol[] {
  const fuzzyQuery = searchTerm.replace(/\s/g, '')
  const key = (r: CodeSymbol) => {
    const score = fuzzyScore(r.name, fuzzyQuery)
    return score > 0 ? {score, text: r.name} : null
  }
  return filterSort(codeSymbols, key, compare)
}
